import { IReferenceType, IOpChangeState, IOpObject, IOpData } from '../util/interfaces'
import { Utils } from '../util/utils'
import { ObjectCustomString } from '../util/object-custom-string'
import { OpDataList } from '../util/op-data-list'
import { Enums } from '../common/enums'

import { OpActor } from './op-actor'
import { OpConsumerData } from './op-consumer-data'
import { OpConsumerType } from './op-consumer-type'
import { OpConsumerPaymentModule } from './op-consumer-payment-module'
import { OpDistributor } from './op-distributor'
import { OpLocation } from './op-location'
import { OpCreditInfo } from '../custom/op-credit-info'

import { DataProvider} from  '../../helpers'


export class OpConsumer implements IReferenceType, IOpObject, IOpChangeState {

    // #region Properties

    public idConsumer: number
    public idConsumerType: number
    public idActor: number
    public idDistributor: number
    public description: string
    public isBlocked: boolean

    // #endregion


    // #region Navigation Properties

    public actor: OpActor
    public consumerType: OpConsumerType
    public distributor: OpDistributor

    // #endregion


    // #region Custom Properties

    public creditInfo: OpCreditInfo
    public paymentModules: OpConsumerPaymentModule[] = []
    public currentLocation: OpLocation

    // #endregion


    // #region Ctor

    constructor(opObj: any = null) {
        this.fromJSON(opObj)
    }

    // #endregion


    // #region IOpChangeState implementation

    public opState: Enums.OpChangeState

    // #endregion


    // #region IReferenceType implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdConsumer
    }

    public getReferenceKey(): number {
        return this.idConsumer
    }

    public getReferenceValue(): any {
        return this
    }

    // #endregion


    // #region IOpObject implementation

    public get idObject(): any {
        return this.idConsumer
    }

    public set idObject(idObject: any) {
        this.idConsumer = idObject
    }

    public dataList: OpDataList<OpConsumerData> = new OpDataList<OpConsumerData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string;

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.idConsumer = opObj.ID_CONSUMER
            this.idConsumerType = opObj.ID_CONSUMER_TYPE
            this.idActor = opObj.ID_ACTOR
            this.isBlocked = opObj.IS_BLOCKED
            this.description = opObj.DESCRIPTION

            this.dynamicValues = opObj.dynamicValues
            this.opState = opObj.OpState

            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpConsumerData(data)))
            }

            this.actor = opObj._Actor != null ? new OpActor(opObj._Actor) : null
            this.consumerType = opObj._ConsumerType != null ? new OpConsumerType(opObj._ConsumerType) : null
            this.creditInfo = opObj.CreditInfo != null ? new OpCreditInfo(opObj.CreditInfo) : null
            this.distributor = opObj._Distributor != null ? new OpDistributor(opObj._Distributor) : null

            if (opObj.PaymentModules) {
                this.paymentModules.push(...opObj.PaymentModules.map((paymentModule: OpConsumerPaymentModule) => new OpConsumerPaymentModule(paymentModule)))
            }

            this.currentLocation = opObj.CurrentLocation != null ? new OpLocation(opObj.CurrentLocation) : null
        } else {
            this.idConsumer = 0

            this.opState = Enums.OpChangeState.New
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_CONSUMER: this.idConsumer,
            ID_CONSUMER_TYPE: this.idConsumerType,
            ID_DISTRIBUTOR: this.idDistributor,
            ID_ACTOR: this.idActor,
            DESCRIPTION: this.description,
            IS_BLOCKED: this.isBlocked,
            _Actor: this.actor != null ? this.actor.toJSON() : null,
            
            DataList: this.dataList ? this.dataList.ToJSON() : null,
            OpState: this.opState,

            PaymentModules: this.paymentModules != null ? this.paymentModules.map((paymentModule: OpConsumerPaymentModule) => paymentModule.toJSON()) : null,

            CurrentLocation: this.currentLocation != null ? this.currentLocation.toJSON() : null
        }
    }

    public toString(): string {
        const customFormatString: string = ObjectCustomString.getCustomStringFormat(this)

        if (!customFormatString) {
            if (this.actor != null) {
                return this.actor.toString()
            }

            return "[undefined]"
        }
        else {
            return ObjectCustomString.getObjectCustomString(customFormatString, this)
        }
    }

    // #endregion


    public static loadNavigationProperties(dataProvider: DataProvider, list: OpConsumer[]): JQueryPromise<OpConsumer[]> {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }

    public static fromIOpObject(object: IOpObject): OpConsumer {
        const newObject: OpConsumer = new OpConsumer(null)

        newObject.idConsumer = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpConsumerData.fromIOpData(data)))

        return newObject
    }


    // #region ICopyable

    public clone(includeNavigationProperties: boolean, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpConsumer {
        const consumer: OpConsumer = new OpConsumer()

        consumer.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return consumer
    }

    public copy(source: OpConsumer, includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean): void {

        if (source == null) {
            return null
        }

        if (includeNavigationProperties) {
            this.actor = source.actor // TODO zaimplementować ICopyable

            this.consumerType = source.consumerType != null ? source.consumerType.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues) :null

            this.distributor = source.distributor != null ? source.distributor.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues) : null
        }

        if (includeCustomProperties) {
            this.creditInfo = source.creditInfo // TODO Zaimplementować ICopyable

            this.paymentModules = source.paymentModules != null
                ? source.paymentModules.map((module: OpConsumerPaymentModule) => module.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
                : null

            this.currentLocation = source.currentLocation // TODO Zaimplementować ICopyable
        }

        if (includeDataList) {
            if (source.dataList != null) {
                source.dataList.toArray().forEach((data: OpConsumerData) => {
                    this.dataList.push(data.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
                })
            }
        }

        if (includeDynamicValues) {
            this.dynamicValues = source.dynamicValues
        }

        this.idConsumer = source.idConsumer
        this.idConsumerType = source.idConsumerType
        this.idActor = source.idActor
        this.idDistributor = source.idDistributor
        this.description = source.description
        this.isBlocked = source.isBlocked

        this.opState = source.opState
    }

    // #endregion 
}